/**
 * RadioBackdrop — particles, floating orbs and the phosphor grid behind the
 * player.
 *
 * Positions are memoised once per mount. v2 recomputed Math.random() on every
 * render, so the whole field jittered each time the 15s metadata poll
 * re-rendered the section.
 */

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const PARTICLE_COUNT = 40;
const ORB_COUNT = 5;

function reducedMotion() {
  return (
    typeof window !== 'undefined' &&
    Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches)
  );
}

export default function RadioBackdrop() {
  const calm = reducedMotion();

  const particles = useMemo(
    () =>
      Array.from({ length: PARTICLE_COUNT }, () => ({
        left: `${Math.random() * 100}%`,
        duration: 14 + Math.random() * 20,
        delay: Math.random() * 10,
        size: Math.random() > 0.85 ? 2 : 1,
      })),
    [],
  );

  const orbs = useMemo(
    () =>
      Array.from({ length: ORB_COUNT }, (_, index) => ({
        size: 100 + index * 50,
        left: `${12 + index * 17}%`,
        top: `${8 + index * 18}%`,
        duration: 15 + index * 3,
      })),
    [],
  );

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {/* Vertical gradient wash */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]" />

      {/* Phosphor grid */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,255,65,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,65,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }}
      />

      {/* Floating orbs */}
      {orbs.map((orb, index) => (
        <motion.div
          key={`orb-${index}`}
          className="absolute rounded-full"
          style={{
            width: orb.size,
            height: orb.size,
            left: orb.left,
            top: orb.top,
            background: 'radial-gradient(circle, rgba(0,255,65,0.1) 0%, transparent 70%)',
          }}
          animate={calm ? undefined : { x: [0, 30, -20, 0], y: [0, -40, 20, 0], scale: [1, 1.1, 0.9, 1] }}
          transition={{ duration: orb.duration, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      {/* Rising dust */}
      {particles.map((particle, index) => (
        <motion.div
          key={`dust-${index}`}
          className="absolute bottom-0 rounded-full bg-[#00FF41]"
          style={{ left: particle.left, width: particle.size, height: particle.size }}
          initial={{ y: 0, opacity: 0 }}
          animate={calm ? { opacity: 0.2 } : { y: '-105vh', opacity: [0, 0.5, 0] }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: 'linear',
          }}
        />
      ))}
    </div>
  );
}
